import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Flex } from '@atlaskit/primitives';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { useAnalytics } from 'src/components/settings/analytics';
import { ConfirmationModal } from 'src/components/settings/modals';
import { useParams } from 'src/router/hooks';

import { useMovePullRequestToTop } from '../hooks';
import { messages } from '../merge-queues.i18n';

export const PullRequestMoveToTopModal = ({
  onClose,
  focusedPullRequest,
  focusedMergeQueue,
}: {
  onClose: () => void;
  focusedPullRequest: number | undefined;
  focusedMergeQueue: MergeQueue | undefined;
}) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'pullRequestMoveToTopModal',
  });
  const { formatMessage } = useIntl();

  const { repositoryOwner: owner, repositorySlug: slug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  const { movePullRequestToTop, isMovingPullRequestToTop } =
    useMovePullRequestToTop({
      mergeQueueUuid: focusedMergeQueue?.uuid || '',
      owner,
      slug,
      onClose,
    });

  const modalBody = useMemo(
    () => (
      <Flex direction="column" gap="space.100">
        {formatMessage(messages.moveToTopPullRequestModalBody)}
      </Flex>
    ),
    [formatMessage]
  );

  return (
    <ConfirmationModal
      appearance="warning"
      header={formatMessage(messages.moveToTopPullRequestModalHeader, {
        pullRequestNumber: focusedPullRequest,
      })}
      body={modalBody}
      onConfirm={() => {
        publishButtonClickEvent('confirmMoveToTop');
        if (focusedPullRequest) {
          movePullRequestToTop(focusedPullRequest);
        }
      }}
      onCancel={onClose}
      isDisabled={!focusedPullRequest}
      isLoading={isMovingPullRequestToTop}
      customButtonText={{
        confirm: formatMessage(messages.moveToTopAction),
        cancel: formatMessage(messages.cancel),
      }}
    />
  );
};
